import { useEffect, useLayoutEffect, useState } from "react";
import { useSearch } from "./useSearch";
import { SearchResults } from "./SearchResults";
import { useCurrentRepo } from "./router";

// CommandPalette is spec §10's "⌘K" command-palette interface: a global
// overlay, mounted once in AppShell so it's available from every
// authenticated page. Opens on Cmd+K/Ctrl+K, closes on Escape, backdrop
// click, or picking a result (SearchResults' onNavigate callback). Search
// is repo-scoped to whichever repo is currently being browsed
// (useCurrentRepo()) — same reasoning as SearchPage.
export function CommandPalette({ token }: { token: string }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const repo = useCurrentRepo();
  const { results, status } = useSearch(token, repo, open ? query : "");

  // A layout effect, not a passive one: the listener must be attached in
  // the same commit that shows the shell, or a Cmd+K pressed before React
  // flushes passive effects is silently dropped. That race made App.test's
  // "opens the command palette from any page via Cmd+K" fail ~1 run in 10.
  useLayoutEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  if (!open) return null;

  return (
    <div className="command-palette-backdrop" data-testid="command-palette-backdrop" onClick={() => setOpen(false)}>
      <div className="command-palette" onClick={(e) => e.stopPropagation()}>
        <input
          type="search"
          role="searchbox"
          aria-label="Search Cortex Atlas"
          placeholder="Search ADRs, capabilities, designs, runbooks, B&IT…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        <div className="command-palette-results">
          {status === "unavailable" || status === "error" ? (
            <p className="muted-note">Search is unavailable right now.</p>
          ) : (
            <SearchResults results={results} onNavigate={() => setOpen(false)} />
          )}
        </div>
        <div className="command-palette-hint">Esc to close</div>
      </div>
    </div>
  );
}
